import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Drawer from '@mui/material/Drawer'
import Toolbar from '@mui/material/Toolbar'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Badge from '@mui/material/Badge'
import DashboardIcon from '@mui/icons-material/Dashboard'
import FolderIcon from '@mui/icons-material/Folder'
import ArticleIcon from '@mui/icons-material/Article'
import DnsIcon from '@mui/icons-material/Dns'
import LanguageIcon from '@mui/icons-material/Language'
import StorefrontIcon from '@mui/icons-material/Storefront'
import InventoryIcon from '@mui/icons-material/Inventory'
import HubIcon from '@mui/icons-material/Hub'
import KeyIcon from '@mui/icons-material/Key'
import TerminalIcon from '@mui/icons-material/Terminal'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import CloudIcon from '@mui/icons-material/Cloud'
import usePendingChangesProjects from '../../hooks/usePendingChangesProjects'
import usePendingChangesPackageImports from '../../hooks/usePendingChangesPackageImports'

const SIDEBAR_WIDTH = 240

function Sidebar({ open, onClose }) {
  const { teamSlug } = useParams()
  const location = useLocation()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { pendingChangesCount } = usePendingChangesProjects()
  const { pendingChangesCount: packageImportsPendingCount } = usePendingChangesPackageImports()

  useEffect(() => {
    if (!isDesktop && onClose) {
      onClose()
    }
  }, [location.pathname])

  const menuGroups = teamSlug
    ? [
        {
          labelId: 'navigation.groups.general',
          items: [
            {
              labelId: 'navigation.dashboard',
              icon: <DashboardIcon />,
              path: `/teams/${teamSlug}`,
              exact: true,
            },
          ],
        },
        {
          labelId: 'navigation.groups.packages',
          items: [
            {
              labelId: 'navigation.packageImports',
              icon: <InventoryIcon />,
              path: `/teams/${teamSlug}/docker/package-imports`,
              badge: packageImportsPendingCount,
            },
            {
              labelId: 'navigation.marketplace',
              icon: <StorefrontIcon />,
              path: `/teams/${teamSlug}/packages/marketplace`,
            },
          ],
        },
        {
          labelId: 'navigation.groups.infrastructure',
          items: [
            {
              labelId: 'navigation.nodes',
              icon: <DnsIcon />,
              path: `/teams/${teamSlug}/nodes`,
            },
            {
              labelId: 'navigation.domains',
              icon: <LanguageIcon />,
              path: `/teams/${teamSlug}/networking/domains`,
            },
            {
              labelId: 'navigation.cloud',
              icon: <CloudIcon />,
              path: `/teams/${teamSlug}/cloud/hetzner`,
            },
          ],
        },
        {
          labelId: 'navigation.groups.docker',
          items: [
            {
              labelId: 'navigation.projects',
              icon: <FolderIcon />,
              path: `/teams/${teamSlug}/docker/projects`,
              badge: pendingChangesCount,
            },
            {
              labelId: 'navigation.networks',
              icon: <HubIcon />,
              path: `/teams/${teamSlug}/docker/networks`,
            },
            {
              labelId: 'navigation.logs',
              icon: <ArticleIcon />,
              path: `/teams/${teamSlug}/docker/logs`,
            },
            {
              labelId: 'navigation.registries',
              icon: <KeyIcon />,
              path: `/teams/${teamSlug}/docker/registries`,
            },
          ],
        },
        {
          labelId: 'navigation.groups.operations',
          items: [
            {
              labelId: 'navigation.terminal',
              icon: <TerminalIcon />,
              path: `/teams/${teamSlug}/operations/terminal`,
            },
            {
              labelId: 'navigation.monitoring',
              icon: <MonitorHeartIcon />,
              path: `/teams/${teamSlug}/operations/monitoring`,
            },
          ],
        },
      ]
    : []

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path
    }
    return location.pathname.startsWith(item.path)
  }

  return (
    <Drawer
      variant={isDesktop ? 'permanent' : 'temporary'}
      open={isDesktop || open}
      onClose={onClose}
      sx={{
        width: { xs: 0, md: SIDEBAR_WIDTH },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          bgcolor: 'navigation.main',
          borderRight: 'none',
        },
      }}
    >
      <Toolbar />
      <Box sx={{ overflow: 'auto', py: 1 }}>
        {teamSlug ? (
          <Box sx={{ px: 1 }}>
            {menuGroups.map((group, groupIndex) => (
              <Box key={group.labelId} sx={{ mb: groupIndex < menuGroups.length - 1 ? 2 : 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.5)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    px: 1,
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  <FormattedMessage id={group.labelId} />
                </Typography>
                <List disablePadding>
                  {group.items.map((item) => (
                    <ListItem key={item.labelId} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        component={RouterLink}
                        to={item.path}
                        selected={isActive(item)}
                        sx={{
                          borderRadius: 1,
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.1)',
                            color: '#fff',
                          },
                          '&.Mui-selected': {
                            bgcolor: 'rgba(255,255,255,0.15)',
                            color: '#fff',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.2)',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={<FormattedMessage id={item.labelId} />} />
                        {item.badge > 0 && (
                          <Badge
                            badgeContent={item.badge}
                            sx={{
                              '& .MuiBadge-badge': {
                                bgcolor: 'pendingChanges.main',
                                color: 'pendingChanges.contrastText',
                              },
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              <FormattedMessage id="navigation.selectTeam" />
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  )
}

export default Sidebar
